import React, { useState, useEffect } from 'react';
import { X, Download, FileText, FileSpreadsheet, Calendar, ChevronDown, Check, Search, Columns } from 'lucide-react';
import { Modal } from "@mui/material";
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';

interface ColumnField {
    field: string;
    label: string;
    type: string;
    default?: boolean;
}

interface ColumnsResponse {
    status: boolean;
    data: {
        report_name?: string;
        [key: string]: any;
    };
}

interface DateFilterValue {
    start: string;
    end: string;
}

interface ExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    templateName: string;
    filters?: Record<string, any>;
    clientId?: string | number;
    asset_type?: string;
}

const AgentExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, templateName, clientId, asset_type }) => {
    const [columns, setColumns] = useState<ColumnsResponse['data'] | null>(null);
    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
    const [format, setFormat] = useState<'csv' | 'excel'>('csv');
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [columnSearch, setColumnSearch] = useState('');

    // Date filter states
    const [selectedDateColumn, setSelectedDateColumn] = useState<string>('');
    const [dateRangeType, setDateRangeType] = useState<string>('');
    const [customStartDate, setCustomStartDate] = useState<string>('');
    const [customEndDate, setCustomEndDate] = useState<string>('');
    const [isDateColumnOpen, setIsDateColumnOpen] = useState(false);
    const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchColumns();
        }
    }, [isOpen, templateName]);

    const fetchColumns = async () => {
        setLoading(true);
        try {
            const response = await baseapi.get(`/export/columns/${templateName}`);
            const resData: ColumnsResponse = response.data;

            if (resData.status && resData.data) {
                let formattedColumns: { [key: string]: ColumnField[] } = {};
                let initialSelected: string[] = [];

                // Find the key that ends with '_view' and is an array (e.g., agents_view)
                const viewKey = Object.keys(resData.data).find(key =>
                    key.endsWith('_view') && Array.isArray(resData.data[key])
                );

                if (viewKey) {
                    const fields = resData.data[viewKey] as ColumnField[];
                    formattedColumns = { [viewKey]: fields };
                    initialSelected = fields
                        .filter(f => f.default)
                        .map(f => `${viewKey}.${f.field}`);
                } else if (Array.isArray(resData.data.columns)) {
                    // Fallback to 'columns' key
                    formattedColumns = { [templateName]: resData.data.columns };
                    initialSelected = resData.data.columns
                        .filter(f => f.default)
                        .map(f => `${templateName}.${f.field}`);
                } else {
                    // Original categorised structure
                    Object.entries(resData.data).forEach(([cat, fields]) => {
                        if (Array.isArray(fields) && cat !== 'report_name') {
                            formattedColumns[cat] = fields;
                            fields.forEach(f => {
                                if (f.default) initialSelected.push(`${cat}.${f.field}`);
                            });
                        }
                    });
                }

                setColumns(formattedColumns);
                setSelectedColumns(initialSelected);
            }
        } catch (error: any) {
            console.error('Error fetching columns:', error);
            showError('Failed to fetch export columns');
        } finally {
            setLoading(false);
        }
    };

    const getDateTimeColumns = (): { category: string; field: string; label: string }[] => {
        if (!columns) return [];

        const dateColumns: { category: string; field: string; label: string }[] = [];

        Object.entries(columns).forEach(([category, fields]) => {
            fields.forEach((field: ColumnField) => {
                if (field.type === 'datetime' || field.type === 'date') {
                    dateColumns.push({
                        category,
                        field: field.field,
                        label: `${field.label}`
                    });
                }
            });
        });

        return dateColumns;
    };

    const getDateRange = (): DateFilterValue | null => {
        const now = new Date();
        let start: Date;
        let end: Date = now;

        switch (dateRangeType) {
            case 'last_7_days':
                start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            case 'last_30_days':
                start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                break;
            case 'custom':
                if (!customStartDate || !customEndDate) return null;
                start = new Date(customStartDate + 'T00:00:00');
                end = new Date(customEndDate + 'T23:59:59.999');
                break;
            default:
                return null;
        }

        return {
            start: start.toISOString(),
            end: end.toISOString()
        };
    };

    const handleColumnToggle = (category: string, field: string) => {
        const columnKey = `${category}.${field}`;
        setSelectedColumns(prev =>
            prev.includes(columnKey)
                ? prev.filter(c => c !== columnKey)
                : [...prev, columnKey]
        );
    };

    const handleSelectAll = (category: string, fields: ColumnField[]) => {
        const categoryColumns = fields.map(f => `${category}.${f.field}`);
        const allSelected = categoryColumns.every(col => selectedColumns.includes(col));

        if (allSelected) {
            setSelectedColumns(prev => prev.filter(c => !categoryColumns.includes(c)));
        } else {
            setSelectedColumns(prev => [...new Set([...prev, ...categoryColumns])]);
        }
    };

    const handleExport = async () => {
        if (selectedColumns.length === 0) {
            showError('Please select at least one column to export');
            return;
        }

        if (selectedDateColumn && dateRangeType === 'custom' && (!customStartDate || !customEndDate)) {
            showError('Please select both start and end dates');
            return;
        }

        setExporting(true);
        try {
            const payload: any = {
                format,
                timezone,
                columns: selectedColumns,
                ...(asset_type && asset_type !== 'all' && { filters: { asset_type } }),
                ...(clientId && { client_id: clientId })

            };

            if (selectedDateColumn && dateRangeType) {
                if (dateRangeType === 'custom') {
                    const dateRange = getDateRange();
                    if (dateRange) {
                        payload.date_filter = {
                            column: selectedDateColumn,
                            operator: 'custom',
                            value: dateRange
                        };
                    }
                } else {
                    payload.date_filter = {
                        column: selectedDateColumn,
                        operator: dateRangeType,
                    };
                }
            }

            if (!clientId) {
                payload.is_global = true
            }

            const response = await baseapi.post(
                `/export/${templateName}`,
                payload, { responseType: 'blob' }

            );

            if (response.data instanceof Blob) {
                const contentType = response.headers['content-type'];
                if (contentType && contentType.includes('application/json')) {
                    const text = await response.data.text();
                    const jsonData = JSON.parse(text);
                    if (jsonData.status === false) {
                        showError(jsonData.message || 'Export failed');
                        return;
                    }
                } else {
                    const url = window.URL.createObjectURL(response.data);
                    const a = document.createElement('a');
                    a.href = url;
                    const now = new Date();
                    const pad = (n: number) => String(n).padStart(2, '0');
                    const timestamp = `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}_${pad(now.getMinutes())}`;
                    a.download = `${templateName}_export_${timestamp}.${format === 'csv' ? 'csv' : 'xlsx'}`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    showSuccess('Export started successfully');
                    onClose();
                }
            }
        } catch (error: any) {
            console.error('Error exporting:', error);
            showError('Export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const dateTimeColumns = getDateTimeColumns();

    return (
        <Modal
            open={isOpen}
            className="flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        >
            <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl animate-in fade-in zoom-in duration-300">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-600/20 rounded-lg">
                            <Download className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white tracking-tight">Export Data</h2>
                            <p className="text-xs text-gray-400">Configure your export for <span className="text-orange-500 font-semibold uppercase">{templateName}</span></p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-800 rounded-full transition-colors group"
                    >
                        <X className="w-5 h-5 text-gray-500 group-hover:text-white" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                    {/* Left Panel: Settings */}
                    <div className="w-full md:w-80 border-r border-gray-800 p-6 space-y-6 overflow-y-auto custom-scroll">
                        {/* Format Selection */}
                        <div className="space-y-3">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">File Format</label>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => setFormat('csv')}
                                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${format === 'csv'
                                        ? 'bg-orange-600/10 border-orange-500 text-white shadow-lg shadow-orange-900/20'
                                        : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
                                        }`}
                                >
                                    <FileText className={`w-4 h-4 ${format === 'csv' ? 'text-orange-500' : 'text-gray-500'}`} />
                                    <span className="text-xs font-bold">CSV</span>
                                </button>
                                <button
                                    onClick={() => setFormat('excel')}
                                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${format === 'excel'
                                        ? 'bg-orange-600/10 border-orange-500 text-white shadow-lg shadow-orange-900/20'
                                        : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
                                        }`}
                                >
                                    <FileSpreadsheet className={`w-4 h-4 ${format === 'excel' ? 'text-orange-500' : 'text-gray-500'}`} />
                                    <span className="text-xs font-bold">Excel</span>
                                </button>
                            </div>
                        </div>

                        {/* Date Filter */}
                        {dateTimeColumns.length > 0 && (
                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                                    <Calendar className="w-3 h-3" />
                                    Date Range Filter
                                </label>

                                <div className="space-y-2">
                                    <div className="relative">
                                        <button
                                            onClick={() => {
                                                setIsDateColumnOpen(!isDateColumnOpen);
                                                setIsDateFilterOpen(false);
                                            }}
                                            className="w-full flex items-center justify-between p-2.5 bg-gray-800/50 border border-gray-700 rounded-lg text-xs text-white hover:border-gray-600 transition-all"
                                        >
                                            <span className="truncate">
                                                {selectedDateColumn ?
                                                    dateTimeColumns.find(c => `${c.category}.${c.field}` === selectedDateColumn)?.label :
                                                    'Select Date Column'
                                                }
                                            </span>
                                            <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isDateColumnOpen ? 'rotate-180' : ''}`} />
                                        </button>

                                        {isDateColumnOpen && (
                                            <div className="absolute z-20 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-40 overflow-y-auto animate-in fade-in slide-in-from-top-1">
                                                <div
                                                    className="px-3 py-2 text-xs text-gray-400 hover:bg-gray-700 cursor-pointer"
                                                    onClick={() => { setSelectedDateColumn(''); setIsDateColumnOpen(false); }}
                                                >
                                                    None
                                                </div>
                                                {dateTimeColumns.map((col) => {
                                                    const val = `${col.category}.${col.field}`;
                                                    return (
                                                        <div
                                                            key={val}
                                                            onClick={() => { setSelectedDateColumn(val); setIsDateColumnOpen(false); }}
                                                            className="flex items-center justify-between px-3 py-2 text-xs text-white hover:bg-orange-600/10 hover:text-orange-500 cursor-pointer"
                                                        >
                                                            {col.label}
                                                            {selectedDateColumn === val && <Check className="w-3 h-3" />}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {selectedDateColumn && (
                                        <div className="relative">
                                            <button
                                                onClick={() => {
                                                    setIsDateFilterOpen(!isDateFilterOpen);
                                                    setIsDateColumnOpen(false);
                                                }}
                                                className="w-full flex items-center justify-between p-2.5 bg-gray-800/50 border border-gray-700 rounded-lg text-xs text-white hover:border-gray-600 transition-all"
                                            >
                                                <span>
                                                    {(() => {
                                                        switch (dateRangeType) {
                                                            case 'last_7_days': return 'Last 7 days';
                                                            case 'last_30_days': return 'Last 30 days';
                                                            case 'custom': return 'Custom Range';
                                                            default: return 'Select Range';
                                                        }
                                                    })()}
                                                </span>
                                                <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isDateFilterOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {isDateFilterOpen && (
                                                <div className="absolute z-20 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl animate-in fade-in slide-in-from-top-1">
                                                    {[
                                                        { id: '', name: 'Clear' },
                                                        { id: 'last_7_days', name: 'Last 7 days' },
                                                        { id: 'last_30_days', name: 'Last 30 days' },
                                                        { id: 'custom', name: 'Custom Range' }
                                                    ].map((range) => (
                                                        <div
                                                            key={range.id}
                                                            onClick={() => { setDateRangeType(range.id); setIsDateFilterOpen(false); }}
                                                            className="flex items-center justify-between px-3 py-2 text-xs text-white hover:bg-orange-600/10 hover:text-orange-500 cursor-pointer"
                                                        >
                                                            {range.name}
                                                            {dateRangeType === range.id && <Check className="w-3 h-3" />}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {selectedDateColumn && dateRangeType === 'custom' && (
                                        <div className="grid grid-cols-1 gap-2 pt-2">
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-gray-500 uppercase px-1">Start Date</label>
                                                <input
                                                    type="date"
                                                    value={customStartDate}
                                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                                    className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg text-xs text-white focus:border-orange-500 outline-none"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-gray-500 uppercase px-1">End Date</label>
                                                <input
                                                    type="date"
                                                    value={customEndDate}
                                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                                    className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg text-xs text-white focus:border-orange-500 outline-none"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Panel: Column Selection */}
                    <div className="flex-1 p-6 flex flex-col min-h-0">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                                <Columns className="w-3 h-3" />
                                Select Columns to Include
                            </label>

                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Search columns..."
                                    value={columnSearch}
                                    onChange={(e) => setColumnSearch(e.target.value)}
                                    className="w-full sm:w-48 bg-gray-800 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white focus:border-orange-500 outline-none transition-all"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto custom-scroll space-y-4 pr-2">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center h-full gap-3 opacity-50">
                                    <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Metadata Loading...</span>
                                </div>
                            ) : columns ? (
                                Object.entries(columns).map(([category, fields]) => {
                                    const filteredFields = fields.filter((f: ColumnField) =>
                                        f.label.toLowerCase().includes(columnSearch.toLowerCase()) ||
                                        f.field.toLowerCase().includes(columnSearch.toLowerCase())
                                    );

                                    if (filteredFields.length === 0) return null;

                                    const categoryColumns = fields.map((f: ColumnField) => `${category}.${f.field}`);
                                    const allSelected = categoryColumns.every((col: string) => selectedColumns.includes(col));
                                    const someSelected = categoryColumns.some((col: string) => selectedColumns.includes(col)) && !allSelected;

                                    return (
                                        <div key={category} className="bg-gray-800/30 border border-gray-800 rounded-xl overflow-hidden shadow-sm">
                                            {/* Category Header */}
                                            <div className="bg-gray-800/50 px-4 py-2 border-b border-gray-800 flex items-center justify-between">
                                                <label className="flex items-center gap-3 cursor-pointer group">
                                                    <div
                                                        onClick={() => handleSelectAll(category, fields)}
                                                        className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${allSelected
                                                            ? 'bg-orange-600 border-orange-600'
                                                            : someSelected
                                                                ? 'bg-orange-600/50 border-orange-600/50'
                                                                : 'border-gray-600 hover:border-gray-400'
                                                            }`}
                                                    >
                                                        {allSelected && <Check className="w-3 h-3 text-white" />}
                                                        {someSelected && <div className="w-2 h-0.5 bg-white rounded-full" />}
                                                    </div>
                                                    <span className="text-[11px] font-black uppercase text-gray-300 group-hover:text-white transition-colors">{category}</span>
                                                </label>
                                                <span className="text-[10px] font-bold text-gray-500">
                                                    {categoryColumns.filter((c: string) => selectedColumns.includes(c)).length} / {fields.length}
                                                </span>
                                            </div>

                                            {/* Fields Grid */}
                                            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                                                {filteredFields.map((field: ColumnField) => {
                                                    const columnKey = `${category}.${field.field}`;
                                                    const isSelected = selectedColumns.includes(columnKey);

                                                    return (
                                                        <label
                                                            key={field.field}
                                                            className="flex items-center gap-3 py-1 group cursor-pointer"
                                                        >
                                                            <div
                                                                onClick={() => handleColumnToggle(category, field.field)}
                                                                className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all ${isSelected
                                                                    ? 'bg-orange-600 border-orange-600'
                                                                    : 'border-gray-700 group-hover:border-gray-500'
                                                                    }`}
                                                            >
                                                                {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                                                            </div>
                                                            <span className={`text-xs transition-colors ${isSelected ? 'text-white font-medium' : 'text-gray-400 group-hover:text-gray-200'}`}>
                                                                {field.label}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full opacity-30 gap-4">
                                    <Columns className="w-12 h-12" />
                                    <span className="text-[10px] font-black uppercase tracking-widest">No columns available</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-800 bg-gray-900/50 flex items-center justify-between">
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Selection Info</span>
                        <div className="flex items-center gap-2">
                            <span className="text-xl font-bold text-white">{selectedColumns.length}</span>
                            <span className="text-xs text-gray-400">columns selected</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="px-5 py-2 text-xs font-bold text-gray-400 hover:text-white transition-colors"
                        >
                            Dismiss
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={exporting || selectedColumns.length === 0}
                            className="flex items-center gap-2 px-8 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-all shadow-lg shadow-orange-900/20 active:scale-95"
                        >
                            {exporting ? (
                                <>
                                    <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4" />
                                    Generate Export
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Progress Bar for Exporting */}
                {exporting && (
                    <div className="h-1 w-full bg-gray-800 overflow-hidden rounded-b-2xl">
                        <div className="h-full bg-orange-600 animate-progress origin-left"></div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default AgentExportModal;
